/**
 * Guardrail Forge
 * 
 * Auto-generates MDC rules, subagents, hooks, and skills based on
 * codebase analysis. Learns project conventions and creates tailored
 * guardrails to prevent AI drift.
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { glob } from 'fast-glob';
import type {
  GuardrailConfig,
  GeneratedRule,
  GeneratedSubagent,
  GeneratedHook,
  GeneratedSkill,
  ConventionRule,
  PatternDefinition,
  TruthpackData,
  RuleCategory,
} from '../types.js';
import { loadTruthpack } from '../scanners/index.js';

interface ForgeResult {
  rules: GeneratedRule[];
  subagents: GeneratedSubagent[];
  hooks: GeneratedHook[];
  skills: GeneratedSkill[];
  conventions: ConventionRule[];
  patterns: PatternDefinition[];
}

interface ProjectAnalysis {
  framework: string;
  language: 'typescript' | 'javascript';
  architecture: string;
  hasTests: boolean;
  hasApi: boolean;
  hasAuth: boolean;
  hasDocs: boolean;
  dependencies: string[];
  fileCount: number;
  conventions: ConventionRule[];
  patterns: PatternDefinition[];
}

export class GuardrailForge {
  private projectRoot: string;
  private config: GuardrailConfig;
  private truthpack: TruthpackData | null = null;

  constructor(projectRoot: string, config: GuardrailConfig) {
    this.projectRoot = projectRoot;
    this.config = config;
  }

  async forge(): Promise<ForgeResult> {
    console.log('🔨 Guardrail Forge starting...');
    
    // Load truthpack if available
    this.truthpack = await loadTruthpack(this.projectRoot);
    
    // Analyze the project
    const analysis = await this.analyzeProject();
    console.log(`📊 Project: ${analysis.framework} (${analysis.language})`);
    console.log(`📁 Files: ${analysis.fileCount}, Architecture: ${analysis.architecture}`);
    
    // Generate all artifacts
    const rules = await this.generateRules(analysis);
    const subagents = await this.generateSubagents(analysis);
    const hooks = await this.generateHooks(analysis);
    const skills = await this.generateSkills(analysis);
    
    // Write all artifacts
    await this.writeArtifacts({ rules, subagents, hooks, skills, conventions: analysis.conventions, patterns: analysis.patterns });
    
    console.log(`✅ Generated: ${rules.length} rules, ${subagents.length} subagents, ${hooks.length} hooks, ${skills.length} skills`);
    
    return {
      rules,
      subagents,
      hooks,
      skills,
      conventions: analysis.conventions,
      patterns: analysis.patterns,
    };
  }

  private async analyzeProject(): Promise<ProjectAnalysis> {
    // Get all source files
    const files = await glob(['**/*.{ts,tsx,js,jsx}'], {
      cwd: this.projectRoot,
      ignore: ['**/node_modules/**', '**/dist/**', '**/build/**', '**/.next/**'],
    });

    // Read package.json for dependencies
    let packageJson: Record<string, unknown> = {};
    try {
      const content = await fs.readFile(path.join(this.projectRoot, 'package.json'), 'utf-8');
      packageJson = JSON.parse(content);
    } catch {
      // No package.json
    }

    const dependencies = [
      ...Object.keys((packageJson.dependencies as Record<string, string>) || {}),
      ...Object.keys((packageJson.devDependencies as Record<string, string>) || {}),
    ];

    // Detect framework
    const framework = this.detectFramework(dependencies, files);
    
    // Detect language
    const hasTypeScript = files.some(f => f.endsWith('.ts') || f.endsWith('.tsx'));
    const language = hasTypeScript ? 'typescript' : 'javascript';
    
    // Detect architecture
    const architecture = this.detectArchitecture(files);
    
    // Detect features
    const hasTests = files.some(f => f.includes('.test.') || f.includes('.spec.') || f.includes('__tests__'));
    const hasApi = files.some(f => f.includes('/api/') || f.includes('routes'));
    const hasAuth = dependencies.some(d => ['next-auth', 'passport', 'jsonwebtoken', 'clerk', 'auth0'].includes(d));
    const hasDocs = await this.hasDocumentation();

    // Learn conventions from code
    const conventions = await this.learnConventions(files);
    const patterns = await this.detectPatterns(files);

    return {
      framework,
      language,
      architecture,
      hasTests,
      hasApi,
      hasAuth,
      hasDocs,
      dependencies,
      fileCount: files.length,
      conventions,
      patterns,
    };
  }

  private detectFramework(dependencies: string[], files: string[]): string {
    if (dependencies.includes('next')) return 'nextjs';
    if (dependencies.includes('react')) return 'react';
    if (dependencies.includes('vue')) return 'vue';
    if (dependencies.includes('express')) return 'express';
    if (dependencies.includes('fastify')) return 'fastify';
    if (dependencies.includes('hono')) return 'hono';
    if (dependencies.includes('@nestjs/core')) return 'nestjs';
    if (files.some(f => f.includes('app/') && f.includes('page.'))) return 'nextjs-app-router';
    return 'unknown';
  }

  private detectArchitecture(files: string[]): string {
    const hasSrc = files.some(f => f.startsWith('src/'));
    const hasFeatures = files.some(f => f.includes('/features/') || f.includes('/modules/'));
    const hasLayers = files.some(f => 
      f.includes('/services/') || f.includes('/repositories/') || f.includes('/controllers/')
    );
    
    if (hasFeatures) return 'feature-based';
    if (hasLayers) return 'layered';
    if (hasSrc) return 'src-based';
    return 'flat';
  }

  private async hasDocumentation(): Promise<boolean> {
    try {
      const docs = await glob(['README.md', 'docs/**/*.md'], { cwd: this.projectRoot });
      return docs.length > 0;
    } catch {
      return false;
    }
  }

  private async learnConventions(files: string[]): Promise<ConventionRule[]> {
    const conventions: ConventionRule[] = [];
    
    // Analyze naming conventions
    const namingRules = await this.analyzeNamingConventions(files);
    if (namingRules.length > 0) {
      conventions.push({ category: 'naming', rules: namingRules });
    }

    // Analyze import conventions
    const importRules = await this.analyzeImportConventions(files);
    if (importRules.length > 0) {
      conventions.push({ category: 'imports', rules: importRules });
    }

    // Analyze error handling
    const errorRules = await this.analyzeErrorHandling(files);
    if (errorRules.length > 0) {
      conventions.push({ category: 'error-handling', rules: errorRules });
    }

    return conventions;
  }

  private async analyzeNamingConventions(files: string[]): Promise<string[]> {
    const rules: string[] = [];
    
    // Check file naming
    const kebabCase = files.filter(f => /^[a-z0-9-]+\.(ts|js|tsx|jsx)$/.test(path.basename(f)));
    const pascalCase = files.filter(f => /^[A-Z][a-zA-Z0-9]+\.(ts|js|tsx|jsx)$/.test(path.basename(f)));
    
    if (kebabCase.length > pascalCase.length * 2) {
      rules.push('Use kebab-case for file names');
    } else if (pascalCase.length > kebabCase.length * 2) {
      rules.push('Use PascalCase for component file names');
    }

    // Check for test file patterns
    const testFiles = files.filter(f => f.includes('.test.') || f.includes('.spec.'));
    if (testFiles.length > 0) {
      const useTest = testFiles.filter(f => f.includes('.test.')).length;
      const useSpec = testFiles.filter(f => f.includes('.spec.')).length;
      
      if (useTest > useSpec * 2) {
        rules.push('Use .test.ts suffix for test files');
      } else if (useSpec > useTest * 2) {
        rules.push('Use .spec.ts suffix for test files');
      }
    }

    return rules;
  }

  private async analyzeImportConventions(files: string[]): Promise<string[]> {
    const rules: string[] = [];
    let typeImports = 0;
    let aliasImports = 0;
    let sampleSize = 0;

    // Sample first 50 files
    const sampleFiles = files.slice(0, 50);
    
    for (const file of sampleFiles) {
      try {
        const fullPath = path.join(this.projectRoot, file);
        const content = await fs.readFile(fullPath, 'utf-8');
        
        if (content.includes('import type')) typeImports++;
        if (content.includes("from '@/") || content.includes('from "~/')) aliasImports++;
        sampleSize++;
      } catch {
        // Skip unreadable files
      }
    }

    if (sampleSize > 0) {
      if (typeImports / sampleSize > 0.3) {
        rules.push('Use import type for type-only imports');
      }
      if (aliasImports / sampleSize > 0.3) {
        rules.push('Use @/ alias for src directory imports');
      }
    }

    return rules;
  }

  private async analyzeErrorHandling(files: string[]): Promise<string[]> {
    const rules: string[] = [];
    let tryCatch = 0;
    let customErrors = 0;
    let sampleSize = 0;

    const sampleFiles = files.slice(0, 50);
    
    for (const file of sampleFiles) {
      try {
        const fullPath = path.join(this.projectRoot, file);
        const content = await fs.readFile(fullPath, 'utf-8');
        
        if (content.includes('try {') && content.includes('catch')) tryCatch++;
        if (content.includes('extends Error')) customErrors++;
        sampleSize++;
      } catch {
        // Skip unreadable files
      }
    }

    if (sampleSize > 0) {
      if (tryCatch / sampleSize > 0.2) {
        rules.push('Always handle async errors with try/catch');
      }
      if (customErrors > 0) {
        rules.push('Use custom error classes for domain errors');
      }
    }

    return rules;
  }

  private async detectPatterns(files: string[]): Promise<PatternDefinition[]> {
    const patterns: PatternDefinition[] = [];

    // Detect service pattern
    const serviceFiles = files.filter(f => f.includes('service') || f.includes('Service'));
    if (serviceFiles.length > 2) {
      patterns.push({
        name: 'service-class',
        category: 'architecture',
        description: 'Service class pattern for business logic',
        example: 'export class UserService { constructor(private db: Database) {} }',
        antiPatterns: ['Putting business logic in controllers', 'Direct database access without service layer'],
      });
    }

    // Detect repository pattern
    const repoFiles = files.filter(f => f.includes('repository') || f.includes('Repository'));
    if (repoFiles.length > 1) {
      patterns.push({
        name: 'repository-pattern',
        category: 'data-access',
        description: 'Repository pattern for data access abstraction',
        example: 'export interface UserRepository { findById(id: string): Promise<User | null>; }',
        antiPatterns: ['Mixing data access with business logic', 'Direct SQL in controllers'],
      });
    }

    // Detect hooks pattern (React)
    const hookFiles = files.filter(f => f.includes('use') && (f.endsWith('.ts') || f.endsWith('.tsx')));
    if (hookFiles.length > 2) {
      patterns.push({
        name: 'custom-hooks',
        category: 'react',
        description: 'Custom hooks for reusable logic',
        example: 'export function useAuth() { const [user, setUser] = useState(null); return { user }; }',
        antiPatterns: ['Duplicate state logic across components', 'Side effects in render'],
      });
    }

    return patterns;
  }

  private async generateRules(analysis: ProjectAnalysis): Promise<GeneratedRule[]> {
    const rules: GeneratedRule[] = [];
    
    // Anti-hallucination rule (ALWAYS include)
    rules.push(this.createAntiHallucinationRule(analysis));
    
    // Architecture rule
    rules.push(this.createArchitectureRule(analysis));
    
    // Framework-specific rules
    if (analysis.framework === 'nextjs' || analysis.framework === 'nextjs-app-router') {
      rules.push(this.createNextJsRule(analysis));
    }
    
    if (analysis.framework === 'react' || analysis.framework.includes('next')) {
      rules.push(this.createReactRule(analysis));
    }
    
    // TypeScript rules
    if (analysis.language === 'typescript') {
      rules.push(this.createTypeScriptRule(analysis));
    }
    
    // Testing rules
    if (analysis.hasTests) {
      rules.push(this.createTestingRule(analysis));
    }
    
    // API rules
    if (analysis.hasApi && this.truthpack) {
      rules.push(this.createApiRule(analysis));
    }
    
    // Auth rules
    if (analysis.hasAuth) {
      rules.push(this.createAuthRule(analysis));
    }

    // Sort by impact
    rules.sort((a, b) => b.impact - a.impact);
    
    // Return top N based on tier
    const tierLimits: Record<string, number> = {
      minimal: 5,
      standard: 10,
      extended: 20,
      comprehensive: 50,
    };
    
    const limit = tierLimits[this.config.forge.tier] || 10;
    return rules.slice(0, limit);
  }

  private createAntiHallucinationRule(analysis: ProjectAnalysis): GeneratedRule {
    const routeCount = this.truthpack?.routes.length || 0;
    const contractCount = this.truthpack?.contracts.length || 0;

    let content = `---
description: Anti-hallucination protocol - prevents AI from inventing code
alwaysApply: true
priority: 100
---

# Anti-Hallucination Protocol

## Verification Before Code Generation
- NEVER invent APIs, methods, or packages that don't exist
- ALWAYS verify imports exist in package.json before using them
- Reference existing codebase patterns before creating new ones

## When Uncertain
Respond with clarification requests, not guesses:
- "I couldn't find [X] in the codebase. Should I create it?"
- "This API signature might have changed. Please verify."

## Mandatory Checks
1. ✓ All imports resolve to real modules
2. ✓ All referenced functions/methods exist
3. ✓ Types match actual implementations
4. ✓ Patterns match existing codebase conventions
`;

    if (routeCount > 0) {
      content += `
## Verified API Routes (${routeCount} total)
Reference \`.vibecheck/truthpack/routes.json\` for verified endpoints.
`;
    }

    if (contractCount > 0) {
      content += `
## Verified Contracts (${contractCount} total)
Reference \`.vibecheck/truthpack/contracts.json\` for verified types.
`;
    }

    content += `
---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`;

    return {
      id: 'anti-hallucination',
      name: 'Anti-Hallucination Protocol',
      category: 'anti-hallucination',
      description: 'Prevents AI from inventing APIs, fabricating data, and making unverified claims',
      content,
      priority: 100,
      impact: 100,
      alwaysApply: true,
    };
  }

  private createArchitectureRule(analysis: ProjectAnalysis): GeneratedRule {
    const content = `---
description: Project architecture guidelines for ${analysis.framework}
alwaysApply: true
priority: 90
---

# Architecture Guidelines

## Project Type: ${analysis.architecture}
## Framework: ${analysis.framework}
## Language: ${analysis.language}

### Directory Structure
${this.generateDirectoryGuidelines(analysis)}

### Import Conventions
${analysis.conventions.find(c => c.category === 'imports')?.rules.map(r => `- ${r}`).join('\n') || '- Use @/ for src directory imports'}

### File Naming
${analysis.conventions.find(c => c.category === 'naming')?.rules.map(r => `- ${r}`).join('\n') || '- Use kebab-case for files'}

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`;

    return {
      id: 'architecture',
      name: 'Architecture Guidelines',
      category: 'architecture',
      description: 'Project structure and architecture patterns',
      content,
      priority: 90,
      impact: 95,
      alwaysApply: true,
    };
  }

  private generateDirectoryGuidelines(analysis: ProjectAnalysis): string {
    if (analysis.architecture === 'feature-based') {
      return `- Organize code by feature/domain
- Each feature should be self-contained
- Shared code goes in \`shared/\` or \`common/\``;
    }
    
    if (analysis.architecture === 'layered') {
      return `- Use layered architecture (controllers, services, repositories)
- Dependencies flow inward (controllers → services → repositories)
- Keep business logic in services`;
    }
    
    return `- Follow existing directory conventions
- Keep related files together
- Use index files for clean exports`;
  }

  private createNextJsRule(analysis: ProjectAnalysis): GeneratedRule {
    const isAppRouter = analysis.framework === 'nextjs-app-router';
    
    return {
      id: 'nextjs',
      name: 'Next.js Conventions',
      category: 'architecture',
      description: 'Next.js specific patterns and conventions',
      content: `---
description: Next.js ${isAppRouter ? 'App Router' : 'Pages Router'} conventions
globs: ["**/*.tsx", "**/*.ts"]
alwaysApply: false
priority: 80
---

# Next.js Conventions

## Router: ${isAppRouter ? 'App Router' : 'Pages Router'}

### File Conventions
${isAppRouter ? `- Use \`page.tsx\` for pages
- Use \`layout.tsx\` for layouts
- Use \`loading.tsx\` for loading states
- Use \`error.tsx\` for error boundaries` : `- Pages in \`pages/\` directory
- API routes in \`pages/api/\`
- Use \`_app.tsx\` for app wrapper`}

### Data Fetching
${isAppRouter ? `- Use Server Components by default
- Use \`'use client'\` directive for client components
- Prefer server actions for mutations` : `- Use \`getServerSideProps\` for SSR
- Use \`getStaticProps\` for SSG
- API routes for client-side data`}

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 80,
      impact: 85,
      globs: ['**/*.tsx', '**/*.ts'],
      alwaysApply: false,
    };
  }

  private createReactRule(analysis: ProjectAnalysis): GeneratedRule {
    return {
      id: 'react',
      name: 'React Best Practices',
      category: 'conventions',
      description: 'React patterns and best practices',
      content: `---
description: React component patterns and hooks
globs: ["**/*.tsx", "**/*.jsx"]
alwaysApply: false
priority: 75
---

# React Best Practices

## Component Patterns
- Prefer functional components with hooks
- Use TypeScript for props interfaces
- Keep components small and focused

## Hooks
- Custom hooks should start with \`use\`
- Don't call hooks conditionally
- Extract complex logic to custom hooks

## State Management
- Lift state up when needed
- Use context for global state sparingly
- Prefer server state libraries (React Query, SWR) for API data

## Performance
- Memoize expensive computations with \`useMemo\`
- Memoize callbacks with \`useCallback\` when passing to children
- Use React.memo for pure components

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 75,
      impact: 80,
      globs: ['**/*.tsx', '**/*.jsx'],
      alwaysApply: false,
    };
  }

  private createTypeScriptRule(analysis: ProjectAnalysis): GeneratedRule {
    return {
      id: 'typescript',
      name: 'TypeScript Standards',
      category: 'conventions',
      description: 'TypeScript type safety rules',
      content: `---
description: TypeScript strict mode and type safety
globs: ["**/*.ts", "**/*.tsx"]
alwaysApply: false
priority: 85
---

# TypeScript Standards

## Type Safety
- NEVER use \`any\` type - use \`unknown\` if uncertain
- Always declare explicit return types for exported functions
- Use \`strict: true\` in tsconfig.json

## Type Definitions
- Prefer \`interface\` for object shapes
- Use \`type\` for unions and primitives
- Export types that are used by other modules

## Best Practices
- Use \`as const\` for literal types
- Avoid type assertions (\`as\`) - fix the types instead
- Use discriminated unions for complex state

## Common Patterns
\`\`\`typescript
// Good: Explicit types
function getUser(id: string): Promise<User> { ... }

// Bad: Implicit any
function getUser(id) { ... }
\`\`\`

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 85,
      impact: 90,
      globs: ['**/*.ts', '**/*.tsx'],
      alwaysApply: false,
    };
  }

  private createTestingRule(analysis: ProjectAnalysis): GeneratedRule {
    const testSuffix = analysis.conventions
      .find(c => c.category === 'naming')
      ?.rules.find(r => r.includes('.test.') || r.includes('.spec.'))
      ?.includes('.spec.') ? '.spec.ts' : '.test.ts';

    return {
      id: 'testing',
      name: 'Testing Guidelines',
      category: 'testing',
      description: 'Testing patterns and conventions',
      content: `---
description: Testing patterns and requirements
globs: ["**/*${testSuffix}", "**/*.spec.ts"]
alwaysApply: false
priority: 70
---

# Testing Guidelines

## File Naming
- Test files: \`*${testSuffix}\`
- Integration tests: \`*.integration${testSuffix}\`

## Test Structure
\`\`\`typescript
describe('ComponentName', () => {
  describe('method or scenario', () => {
    it('should do something specific', () => {
      // Arrange
      // Act
      // Assert
    });
  });
});
\`\`\`

## Requirements
- All new features must have tests
- All bug fixes must have regression tests
- Test edge cases and error conditions

## Forbidden
- No skipped tests without explanation
- No tests that depend on execution order
- No tests that mutate global state

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 70,
      impact: 75,
      globs: [`**/*${testSuffix}`, '**/*.spec.ts'],
      alwaysApply: false,
    };
  }

  private createApiRule(analysis: ProjectAnalysis): GeneratedRule {
    const routeCount = this.truthpack?.routes.length || 0;
    const routes = this.truthpack?.routes.slice(0, 10) || [];

    return {
      id: 'api',
      name: 'API Conventions',
      category: 'architecture',
      description: 'API route patterns and conventions',
      content: `---
description: API route patterns (${routeCount} verified routes)
globs: ["**/api/**/*.ts", "**/routes/**/*.ts"]
alwaysApply: false
priority: 80
---

# API Conventions

## Verified Routes (${routeCount} total)
${routes.map(r => `- \`${r.method} ${r.path}\` → ${r.file}:${r.line}`).join('\n')}
${routeCount > 10 ? `\n... and ${routeCount - 10} more in .vibecheck/truthpack/routes.json` : ''}

## Pattern Requirements
- Use consistent HTTP methods (GET for read, POST for create, etc.)
- Always validate request body with schemas
- Return consistent response shapes
- Handle errors with proper status codes

## Response Format
\`\`\`typescript
// Success
{ success: true, data: T }

// Error
{ success: false, error: { code: string, message: string } }
\`\`\`

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 80,
      impact: 85,
      globs: ['**/api/**/*.ts', '**/routes/**/*.ts'],
      alwaysApply: false,
    };
  }

  private createAuthRule(analysis: ProjectAnalysis): GeneratedRule {
    const authRules = this.truthpack?.authRules || [];
    
    return {
      id: 'auth',
      name: 'Authentication & Authorization',
      category: 'security',
      description: 'Auth patterns and security requirements',
      content: `---
description: Authentication and authorization patterns
globs: ["**/auth/**/*.ts", "**/middleware/**/*.ts"]
alwaysApply: false
priority: 95
---

# Authentication & Authorization

## Verified Auth Rules
${authRules.slice(0, 10).map(r => `- \`${r.name}\` (${r.type}) → ${r.file}`).join('\n') || '- No auth rules detected yet'}

## Security Requirements
- NEVER commit secrets to code
- ALWAYS validate authentication before accessing protected resources
- Use environment variables for sensitive configuration
- Implement proper session management

## Protected Routes
- All routes under \`/api/admin/*\` require authentication
- Check authorization (roles/permissions) after authentication
- Log security-relevant events

## Common Middleware
\`\`\`typescript
// Auth middleware pattern
export async function authenticate(req, res, next) {
  const token = req.headers.authorization?.split(' ')[1];
  if (!token) return res.status(401).json({ error: 'Unauthorized' });
  // Verify token...
}
\`\`\`

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*`,
      priority: 95,
      impact: 95,
      globs: ['**/auth/**/*.ts', '**/middleware/**/*.ts'],
      alwaysApply: false,
    };
  }

  private async generateSubagents(analysis: ProjectAnalysis): Promise<GeneratedSubagent[]> {
    const subagents: GeneratedSubagent[] = [];

    // Code reviewer subagent
    subagents.push({
      id: 'code-reviewer',
      name: 'Code Reviewer',
      description: 'Reviews code for quality, security, and best practices',
      capabilities: ['code-review', 'security-audit', 'performance-check'],
      content: `# Code Reviewer Agent

## Role
Principal-level code reviewer with security audit capabilities.

## Review Checklist
- [ ] Code follows ${analysis.framework} best practices
- [ ] TypeScript types are correct and complete
- [ ] Error handling is comprehensive
- [ ] Security vulnerabilities addressed
- [ ] Performance considerations met
- [ ] Tests cover critical paths

## Security Focus
- Check for SQL injection vulnerabilities
- Verify authentication/authorization
- Look for sensitive data exposure
- Review dependency versions

## Output Format
Provide structured review with:
1. Summary (approve/request changes)
2. Critical issues (must fix)
3. Suggestions (nice to have)
4. Positive observations
`,
    });

    // Debugger subagent
    subagents.push({
      id: 'debugger',
      name: 'Debugger',
      description: 'Systematic debugging with root cause analysis',
      capabilities: ['debugging', 'root-cause-analysis', 'log-analysis'],
      content: `# Debugger Agent

## Role
Master debugger specializing in systematic root cause analysis.

## Debugging Process
1. **Reproduce** - Understand how to trigger the issue
2. **Isolate** - Narrow down the affected code
3. **Identify** - Find the root cause
4. **Fix** - Propose minimal fix
5. **Verify** - Ensure fix works and doesn't break other things

## Evidence Collection
- Stack traces
- Log output
- State snapshots
- Network requests

## Output Format
1. Root cause (1-2 sentences)
2. Evidence supporting diagnosis
3. Proposed fix with code
4. Test to verify fix
`,
    });

    return subagents;
  }

  private async generateHooks(analysis: ProjectAnalysis): Promise<GeneratedHook[]> {
    const hooks: GeneratedHook[] = [];

    // Pre-commit hook
    hooks.push({
      id: 'pre-commit',
      name: 'Pre-Commit Validation',
      trigger: 'pre-commit',
      description: 'Validates code before committing',
      content: `# Pre-Commit Hook

## Checks to Run
1. **Lint Check** - Run ESLint/Prettier
2. **Type Check** - Run TypeScript compiler
3. **Test Check** - Run affected tests
4. **Secret Scan** - Check for exposed secrets

## Blocking Conditions
- Any lint errors
- TypeScript errors
- Test failures
- Detected secrets

## Commands
\`\`\`bash
npm run lint
npm run typecheck
npm run test:affected
\`\`\`
`,
    });

    // Save hook for continuous validation
    hooks.push({
      id: 'on-save',
      name: 'On-Save Validation',
      trigger: 'save',
      description: 'Validates code on file save',
      content: `# On-Save Hook

## Quick Checks (< 2s)
1. Format file with Prettier
2. Run ESLint on saved file
3. Check for obvious anti-patterns

## Deferred Checks
1. Update truthpack if API route changed
2. Check for duplicate files/content
3. Validate imports exist
`,
    });

    return hooks;
  }

  private async generateSkills(analysis: ProjectAnalysis): Promise<GeneratedSkill[]> {
    const skills: GeneratedSkill[] = [];

    // API builder skill
    if (analysis.hasApi) {
      skills.push({
        id: 'api-builder',
        name: 'API Builder',
        description: 'Creates API endpoints following project conventions',
        trigger: 'building API, creating endpoint, REST route',
        content: `# API Builder Skill

## When to Use
When creating new API endpoints, routes, or handlers.

## Project API Conventions
- Framework: ${analysis.framework}
${this.truthpack?.routes.length ? `- Existing routes: ${this.truthpack.routes.length}` : ''}

## Endpoint Creation Process
1. Check if similar endpoint exists (no duplicates!)
2. Follow existing route patterns
3. Add request validation
4. Implement proper error handling
5. Add to appropriate router

## Template
\`\`\`typescript
export async function handler(req: Request, res: Response) {
  try {
    // Validate input
    const validated = schema.parse(req.body);
    
    // Process
    const result = await service.process(validated);
    
    // Respond
    res.json({ success: true, data: result });
  } catch (error) {
    res.status(500).json({ success: false, error: error.message });
  }
}
\`\`\`
`,
      });
    }

    return skills;
  }

  private async writeArtifacts(result: ForgeResult): Promise<void> {
    const rulesDir = path.join(this.projectRoot, '.cursor', 'rules');
    const agentsDir = path.join(this.projectRoot, '.cursor', 'agents');
    const hooksDir = path.join(this.projectRoot, '.cursor', 'hooks');
    const skillsDir = path.join(this.projectRoot, '.cursor', 'skills');
    const knowledgeDir = path.join(this.projectRoot, '.vibecheck', 'knowledge');

    // Create directories
    await Promise.all([
      fs.mkdir(rulesDir, { recursive: true }),
      fs.mkdir(agentsDir, { recursive: true }),
      fs.mkdir(hooksDir, { recursive: true }),
      fs.mkdir(skillsDir, { recursive: true }),
      fs.mkdir(knowledgeDir, { recursive: true }),
    ]);

    // Write rules
    for (const rule of result.rules) {
      await fs.writeFile(
        path.join(rulesDir, `${rule.id}.mdc`),
        rule.content
      );
    }

    // Write subagents
    for (const agent of result.subagents) {
      await fs.writeFile(
        path.join(agentsDir, `${agent.id}.md`),
        agent.content
      );
    }

    // Write hooks
    for (const hook of result.hooks) {
      await fs.writeFile(
        path.join(hooksDir, `${hook.id}.md`),
        hook.content
      );
    }

    // Write skills
    for (const skill of result.skills) {
      const skillDir = path.join(skillsDir, skill.id);
      await fs.mkdir(skillDir, { recursive: true });
      await fs.writeFile(
        path.join(skillDir, 'SKILL.md'),
        skill.content
      );
    }

    // Write conventions
    await fs.writeFile(
      path.join(knowledgeDir, 'conventions.json'),
      JSON.stringify({ version: '1.0.0', conventions: result.conventions }, null, 2)
    );

    // Write patterns
    await fs.writeFile(
      path.join(knowledgeDir, 'patterns.json'),
      JSON.stringify({ version: '1.0.0', patterns: result.patterns }, null, 2)
    );

    // Write .cursorrules
    await this.writeCursorRules(result);
  }

  private async writeCursorRules(result: ForgeResult): Promise<void> {
    const content = `# Guardrail AI - Project Context

> Generated by Guardrail Forge - Context-aware AI guardrails

## Quick Reference

${result.rules.slice(0, 6).map(r => `- **${r.name}** (Impact: ${r.impact}) - ${r.description}`).join('\n')}

## Anti-Hallucination Protocol (CRITICAL)

Before generating ANY code:
1. Verify imports exist in package.json
2. Confirm referenced functions/methods exist
3. Check types match actual implementations
4. Reference existing patterns before creating new ones

When uncertain, ASK - don't guess.

## Detailed Rules

See \`.cursor/rules/\` directory for detailed MDC specifications.

## Subagents

${result.subagents.map(a => `- **${a.name}** - ${a.description}`).join('\n')}

## Skills

${result.skills.map(s => `- **${s.name}** - ${s.description}`).join('\n')}

---
*Generated at: ${new Date().toISOString()}*
*Total Rules: ${result.rules.length} | Subagents: ${result.subagents.length} | Skills: ${result.skills.length}*

---
<!-- guardrail:auto-generated -->
*Verified by Guardrail ✓*
`;

    await fs.writeFile(
      path.join(this.projectRoot, '.cursorrules'),
      content
    );
  }
}

export async function forge(projectRoot: string, config: GuardrailConfig): Promise<ForgeResult> {
  const forgeInstance = new GuardrailForge(projectRoot, config);
  return forgeInstance.forge();
}
